import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import {
  ApiResponse,
  Client,
  HistoryAction,
  PageableDto,
  PageResponse,
} from '@shared-models';
import { API_CONFIG, ApiConfig } from '../api-config';
import {
  mapMutationMessage,
  mapPageableContent,
  mapPageableResponse,
} from '../../utils/map-pageable';

/** @deprecated Usar `PageResponse<Client>` de `@shared-models`. */
export type CustomerPageResponse = PageResponse<Client>;

/**
 * Respuesta completa del endpoint GET /api/customers/{id}
 * (detail/edit/view del cliente)
 */
export interface CustomerDetail {
  id: string;
  /** Código de negocio del cliente (ej. "c0001"). Puede venir vacío. */
  code?: string;
  name: string;
  lastName: string;
  secondLastName: string;
  fullName: string;
  birthdate: string;
  birthdatePlace: string;
  /** @deprecated Preferir `contacts`. Se mantiene por compatibilidad de lectura. */
  email?: string;
  /** @deprecated Preferir `contacts`. */
  phoneNumber?: string;
  /** @deprecated Preferir `contacts`. */
  lada?: string;
  contacts?: Array<{
    lada?: string;
    phoneNumber?: string;
    phoneTypeId?: string;
    telephonyId?: string;
    companyId?: string;
    preferred?: boolean;
    /** @deprecated Campos del shape anterior (persona de contacto). */
    name?: string;
    email?: string;
    position?: string;
    primary?: boolean;
  }>;
  genderId: string;
  genderName: string;
  maritalStatusId: string;
  maritalStatusName: string;
  nationalityId: string;
  nationalityName: string;
  residenceId: string;
  residenceName: string;
  typePeopleId: string;
  typeOfTaxpayerId: string;
  typeAccountClientId: string;
  taxCategoryId: string;
  regimenId: string;
  peopleActivityId: string;
  pepDirect: boolean;
  pepIndirect: boolean;
  subject: boolean;
  homonyms: boolean;
  live: boolean;
  status: string;
  roles: string[];
  registered: boolean;
  locked: boolean;
  isEmailVerified: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Campo de la respuesta GET /customers/{id}/nosis */
export interface CustomerNosisFieldDto {
  field: string;
  value: string;
}

/** Respuesta data de GET /customers/{id}/nosis */
export interface CustomerNosisDto {
  customerId?: string;
  source?: string;
  businessName?: string;
  obligatedSubject?: boolean;
  pep?: boolean;
  laftHomonymsCount?: number;
  laftHomonymsUrl?: string;
  fields?: CustomerNosisFieldDto[];
}

@Injectable({ providedIn: 'root' })
export class CustomerService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/customers`;
  }

  /**
   * Normaliza el status del API al modelo del frontend.
   * API: "ACTIVE" | "INACTIVE" (u otras variantes) → 'active' | 'inactive'
   */
  private normalizeStatus(
    status: string | undefined | null,
  ): 'active' | 'inactive' {
    if (status == null || String(status).trim() === '') return 'active';
    const upper = String(status).trim().toUpperCase();
    if (upper === 'INACTIVE' || upper === 'INACTIVO') return 'inactive';
    return 'active';
  }

  /**
   * Transforma la respuesta del backend al modelo del frontend.
   * Listado `/api/customers`: usa `id` y `status` del API.
   */
  private mapToClient(item: Record<string, unknown>): Client {
    return {
      id: (item['id'] as string) ?? '',
      code: (item['code'] as string) || '',
      name: (item['name'] as string) || '',
      nationality: (item['nationality'] as string) || '',
      accountType:
        (item['accountType'] as Client['accountType']) || 'Personal',
      documentType: (item['document'] as string) || '',
      documentNumber: String(item['documentNumber'] ?? ''),
      sentTransfers: (item['moneyOrdersSent'] as number) ?? 0,
      receivedTransfers: (item['moneyOrdersReceived'] as number) ?? 0,
      status: this.normalizeStatus(item['status'] as string | undefined),
    };
  }

  /**
   * Obtiene clientes con paginación server-side
   */
  getAll(
    page = 0,
    size = 20,
    sort = 'createdAt',
    direction: 'asc' | 'desc' = 'desc',
    search?: string,
    status?: string,
  ): Observable<PageResponse<Client>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', sort)
      .set('direction', direction);

    if (search && search.trim()) {
      params = params.set('search', search.trim());
    }
    if (status && status.trim()) {
      params = params.set('status', status.trim());
    }

    return this.httpClient
      .get<ApiResponse<PageableDto>>(this.apiUrl, { params })
      .pipe(
        map((response) =>
          mapPageableResponse(
            response,
            (item) => this.mapToClient(item as Record<string, unknown>),
            size,
          ),
        ),
      );
  }

  /**
   * Obtiene un cliente por ID (detail completo para edit/view)
   */
  getById(id: string): Observable<CustomerDetail | undefined> {
    return this.httpClient
      .get<ApiResponse<CustomerDetail>>(`${this.apiUrl}/${id}`)
      .pipe(map((response) => response.data));
  }

  /**
   * Crea un nuevo cliente
   */
  create(data: Record<string, unknown>): Observable<{ message: string }> {
    return this.httpClient.post<ApiResponse<unknown>>(this.apiUrl, data).pipe(
      map((response) =>
        mapMutationMessage(response, 'Cliente creado exitosamente'),
      ),
    );
  }

  /**
   * Actualiza un cliente existente
   */
  update(
    id: string,
    data: Record<string, unknown>,
  ): Observable<{ message: string }> {
    return this.httpClient
      .put<ApiResponse<unknown>>(`${this.apiUrl}/${id}`, data)
      .pipe(
        map((response) =>
          mapMutationMessage(response, 'Cliente actualizado exitosamente'),
        ),
      );
  }

  /**
   * Elimina un cliente
   */
  delete(id: string): Observable<{ message: string }> {
    return this.httpClient
      .delete<{ success: boolean; message: string }>(`${this.apiUrl}/${id}`)
      .pipe(
        map((response) =>
          mapMutationMessage(response, 'Cliente eliminado exitosamente'),
        ),
      );
  }

  /**
   * Actualiza el status de un cliente
   */
  updateStatus(
    id: number | string,
    status: 'active' | 'inactive',
  ): Observable<{ message: string }> {
    const apiStatus = status === 'inactive' ? 'INACTIVE' : 'ACTIVE';
    return this.httpClient
      .patch<{
        success: boolean;
        message: string;
      }>(`${this.apiUrl}/${id}/status`, { status: apiStatus })
      .pipe(
        map((response) =>
          mapMutationMessage(response, 'Status actualizado exitosamente'),
        ),
      );
  }

  /**
   * Exporta el listado de clientes a PDF.
   * GET /api/export/pdf/customer?title=&status=&search=&page=&size=
   */
  exportPdf(options?: {
    title?: string;
    status?: string;
    search?: string;
    page?: number;
    size?: number;
  }): Observable<Blob> {
    const params = new HttpParams()
      .set('title', options?.title || 'Reporte de clientes')
      .set('status', options?.status || '')
      .set('search', options?.search || '')
      .set('page', String(options?.page ?? 0))
      .set('size', String(options?.size ?? 1000));

    return this.httpClient.get(`${this.apiConfig.apiUrl}/export/pdf/customer`, {
      params,
      responseType: 'blob',
    });
  }

  /**
   * Exporta el listado de clientes a Excel.
   * GET /api/export/excel/customer?title=&status=&search=&page=&size=
   */
  exportExcel(options?: {
    title?: string;
    status?: string;
    search?: string;
    page?: number;
    size?: number;
  }): Observable<Blob> {
    const params = new HttpParams()
      .set('title', options?.title || 'Reporte de clientes')
      .set('status', options?.status || '')
      .set('search', options?.search || '')
      .set('page', String(options?.page ?? 0))
      .set('size', String(options?.size ?? 1000));

    return this.httpClient.get(
      `${this.apiConfig.apiUrl}/export/excel/customer`,
      { params, responseType: 'blob' },
    );
  }

  /**
   * Historial de acciones del cliente.
   * GET /api/customers/{id}/history?page=&size=&sort=&direction=
   */
  getHistory(
    id: string,
    page = 0,
    size = 20,
    sort = 'createdAt',
    direction: 'asc' | 'desc' = 'desc',
  ): Observable<HistoryAction[]> {
    const params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', sort)
      .set('direction', direction);

    return this.httpClient
      .get<ApiResponse<PageableDto<HistoryAction>>>(
        `${this.apiUrl}/${id}/history`,
        { params },
      )
      .pipe(map((response) => mapPageableContent(response)));
  }

  /**
   * Consulta NOSIS / compliance del cliente (drawer Detalle).
   * GET /api/customers/{id}/nosis
   */
  getNosis(customerId: string): Observable<CustomerNosisDto> {
    return this.httpClient
      .get<ApiResponse<CustomerNosisDto>>(`${this.apiUrl}/${customerId}/nosis`)
      .pipe(map((response) => response.data as CustomerNosisDto));
  }

  /**
   * Exporta el historial a PDF.
   * GET /api/export/pdf/history/customer/{id}?title=...
   */
  exportHistoryPdf(
    id: string,
    options?: { title?: string },
  ): Observable<Blob> {
    const params = new HttpParams().set(
      'title',
      options?.title || 'Historial de clientes',
    );

    return this.httpClient.get(
      `${this.apiConfig.apiUrl}/export/pdf/history/customer/${id}`,
      { params, responseType: 'blob' },
    );
  }

  /**
   * Exporta el historial a Excel.
   * GET /api/export/excel/history/customer/{id}?title=...
   */
  exportHistoryExcel(
    id: string,
    options?: { title?: string },
  ): Observable<Blob> {
    const params = new HttpParams().set(
      'title',
      options?.title || 'Historial de clientes',
    );

    return this.httpClient.get(
      `${this.apiConfig.apiUrl}/export/excel/history/customer/${id}`,
      { params, responseType: 'blob' },
    );
  }
}
